import { useEffect, useRef, useState } from 'react';

/**
 * ConfirmDialog — destructive-action confirmation.
 *
 * For "Delete template", "Delete folder", and anything else irreversible.
 * The primary action is focus-trapped to the Confirm button (not the
 * Cancel one) so Enter commits — but only after the user can read the
 * body. When `requireTyping` is supplied the Confirm button stays
 * disabled until the user types the matching string, which stops
 * accidental hits on 300+-template cascades.
 */
export interface ConfirmDialogProps {
  title: string;
  /** The body text. Use a ReactNode so callers can emphasise parts. */
  message: React.ReactNode;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  destructive?: boolean | undefined;
  /** When set, the Confirm button is disabled until the user types this
   *  exact string. Typical use: confirm folder deletion by typing its name. */
  requireTyping?: string | undefined;
  onConfirm(): void | Promise<void>;
  onCancel(): void;
}

export function ConfirmDialog(props: ConfirmDialogProps): JSX.Element {
  const {
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = true,
    requireTyping,
    onConfirm,
    onCancel,
  } = props;

  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const typeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (requireTyping && typeRef.current) typeRef.current.focus();
    else if (!requireTyping && confirmRef.current) confirmRef.current.focus();
  }, [requireTyping]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const typingOk = !requireTyping || typed === requireTyping;
  const canConfirm = !busy && typingOk;

  async function doConfirm(): Promise<void> {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError((err as Error).message || 'Action failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div
        className="modal modal--confirm"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-labelledby="confirm-title"
      >
        <header className="modal-header">
          <div>
            <h2 id="confirm-title">{title}</h2>
          </div>
          <button
            type="button"
            className="btn-icon"
            onClick={onCancel}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>
        <div className="modal-body">
          <div className="confirm-body">{message}</div>
          {requireTyping && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <label className="form-label" htmlFor="confirm-type">
                Type <code>{requireTyping}</code> to confirm
              </label>
              <input
                id="confirm-type"
                ref={typeRef}
                className="form-input"
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
            </div>
          )}
          {error && (
            <div className="form-hint form-hint--error" style={{ marginTop: 'var(--space-2)' }}>
              {error}
            </div>
          )}
        </div>
        <footer className="modal-footer">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`btn ${destructive ? 'btn--danger' : 'btn--primary'}`}
            disabled={!canConfirm}
            onClick={doConfirm}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
